import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError, apiGet, apiPatch, apiPost } from '../api/client.js';
import { useAuth } from '../auth/AuthProvider.js';
import { Layout } from '../components/Layout.js';
import { Loader2Icon, UploadCloudIcon } from '../components/icons.js';

/**
 * Admin: Template Builder (extractor v2, Slice 4).
 *
 * Self-service authoring of extractor templates: upload an example vendor
 * workbook, pick the OneView destination format, and let the AI propose a
 * declarative transform recipe. The preview runs the REAL extractor pipeline
 * against the example (draft template + actual Cloud Run job), so what you
 * see is exactly what a production run will produce. Iterate with natural-
 * language feedback until the output matches, then activate the template.
 *
 * kb_admin only — this authors platform-wide templates.
 */

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

interface DestinationFormat {
  id: string;
  code: string;
  displayName: string;
  description: string;
  inputColumns: Array<{ name: string; type?: string; required?: boolean; isKey?: boolean }>;
  fileType: string;
  firstDataRow: number;
  reshapeHint: string;
  notes: string;
}

interface Proposal {
  recipe: Record<string, unknown>;
  rationale: string;
  warnings: string[];
}

/** Unsaved destination-format draft returned by /destination-formats/from-kb
 *  or /parse-xml — reviewed and edited here before the create call. */
interface FormatDraft {
  code: string;
  displayName: string;
  description: string;
  transformationXml: string;
  inputColumns: Array<{ name: string; type?: string; required?: boolean; notes?: string }>;
  fileType: string;
  firstDataRow: number;
  reshapeHint: string;
  notes: string;
  status: string;
  warnings?: string[];
}

interface RunDoc {
  id: string;
  status: string;
  error?: string;
  crptflStats?: Record<string, number>;
}

interface PreviewRow {
  cells: Record<string, string>;
}

type Phase = 'idle' | 'uploading' | 'analyzing' | 'saving_template' | 'previewing' | 'refining';

const PHASE_LABEL: Record<Phase, string> = {
  idle: '',
  uploading: 'Uploading example…',
  analyzing: 'AI is analysing the format…',
  saving_template: 'Saving draft template…',
  previewing: 'Running the extractor pipeline on your example…',
  refining: 'AI is applying your feedback…',
};

/**
 * Journey stepper — the builder is a 4-stage loop (example → AI proposal →
 * pipeline preview/refine → activate) but most of it is invisible until you
 * get there. The stepper keeps the whole journey on screen with honest time
 * expectations, so the first-time admin knows what the grey button leads to.
 */
const BUILDER_STEPS = [
  { label: 'Example & destination', hint: 'pick a real vendor file' },
  { label: 'AI proposal', hint: 'about a minute' },
  { label: 'Pipeline preview & refine', hint: 'real extractor run' },
  { label: 'Activate', hint: 'live for everyone' },
] as const;

function BuilderStepper(props: { current: number; done: number[] }): JSX.Element {
  return (
    <ol className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-2">
      {BUILDER_STEPS.map((step, i) => {
        const n = i + 1;
        const isDone = props.done.includes(n);
        const isCurrent = props.current === n;
        return (
          <li key={step.label} className="flex items-center gap-2">
            <span
              className={
                'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium ' +
                (isDone
                  ? 'bg-nebula-primary text-white'
                  : isCurrent
                    ? 'border-2 border-nebula-primary bg-white text-nebula-primary'
                    : 'border border-nebula-border bg-white text-nebula-fg-3')
              }
            >
              {isDone ? '✓' : n}
            </span>
            <span className="flex flex-col leading-tight">
              <span
                className={
                  'text-sm ' +
                  (isCurrent
                    ? 'font-medium text-nebula-fg-1'
                    : isDone
                      ? 'text-nebula-fg-2'
                      : 'text-nebula-fg-3')
                }
              >
                {step.label}
              </span>
              <span className="text-xs text-nebula-fg-4">{step.hint}</span>
            </span>
            {n < BUILDER_STEPS.length && (
              <span aria-hidden className="mx-1 hidden h-px w-8 bg-nebula-border sm:block" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function slugify(s: string): string {
  return (
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'template'
  );
}

// Must mirror backend env.EXTRACTOR_MAX_BYTES (and Extractor.tsx MAX_BYTES) —
// the signed URL's signature covers this exact header value, so a mismatch or
// omission makes GCS reject the PUT with 400 (first click-test, 2026-07-14).
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB

async function putToGcs(uploadUrl: string, file: File, contentType: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': contentType,
      'x-goog-content-length-range': `1,${MAX_BYTES}`,
    },
    body: file,
  });
  if (!res.ok) throw new Error(`upload failed: HTTP ${res.status}`);
}

export function AdminTemplateBuilderPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === 'kb_admin';

  const [destinations, setDestinations] = useState<DestinationFormat[]>([]);
  const [destId, setDestId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [vendor, setVendor] = useState('');

  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  // Builder-example GCS path — minted once per selected file, reused across
  // refine iterations so we don't re-upload on every round.
  const gcsPathRef = useRef<string | null>(null);

  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [showRecipe, setShowRecipe] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [run, setRun] = useState<RunDoc | null>(null);
  const [previewRows, setPreviewRows] = useState<PreviewRow[]>([]);
  const [feedback, setFeedback] = useState('');
  const [iteration, setIteration] = useState(0);
  const [activated, setActivated] = useState(false);

  // "Add destination format" panel — pull an import definition from the KB by
  // its OneView file reference, or paste the <Transformation> XML directly.
  const [addOpen, setAddOpen] = useState(false);
  const [kbCode, setKbCode] = useState('');
  const [pasteXml, setPasteXml] = useState('');
  const [draft, setDraft] = useState<FormatDraft | null>(null);
  const [draftBusy, setDraftBusy] = useState<'idle' | 'kb' | 'parse' | 'create'>('idle');
  const [draftMsg, setDraftMsg] = useState<string | null>(null);

  const loadFormats = (selectId?: string): Promise<void> =>
    apiGet<{ formats: DestinationFormat[] }>('/api/v1/extractor/destination-formats?status=active')
      .then((r) => {
        const list = r.formats ?? [];
        setDestinations(list);
        if (selectId && list.some((d) => d.id === selectId)) setDestId(selectId);
        else if (!destId && list.length > 0) setDestId(list[0].id);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'Could not load destination formats.'),
      );

  // Load once on mount; loadFormats is stable for this page's lifetime.
  useEffect(() => {
    void loadFormats();
  }, []);

  const destination = useMemo(
    () => destinations.find((d) => d.id === destId) ?? null,
    [destinations, destId],
  );

  const busy = phase !== 'idle';

  const fail = (err: unknown, fallback: string): void => {
    setPhase('idle');
    setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : fallback);
  };

  // ---- Add destination format (from KB / from XML) ---------------------------

  const fetchFromKb = async (): Promise<void> => {
    if (!kbCode.trim()) return;
    setDraftMsg(null);
    setDraft(null);
    setDraftBusy('kb');
    try {
      const r = await apiPost<{ draft: FormatDraft }>(
        '/api/v1/extractor/destination-formats/from-kb',
        { code: kbCode.trim() },
      );
      setDraft(r.draft);
      setDraftBusy('idle');
    } catch (err) {
      setDraftBusy('idle');
      setDraftMsg(
        err instanceof ApiError
          ? err.message
          : 'KB lookup failed — paste the Transformation XML below instead.',
      );
    }
  };

  const parsePastedXml = async (): Promise<void> => {
    if (!pasteXml.trim()) return;
    setDraftMsg(null);
    setDraft(null);
    setDraftBusy('parse');
    try {
      const r = await apiPost<{ draft: FormatDraft }>(
        '/api/v1/extractor/destination-formats/parse-xml',
        { xml: pasteXml.trim() },
      );
      setDraft(r.draft);
      setDraftBusy('idle');
    } catch (err) {
      setDraftBusy('idle');
      setDraftMsg(err instanceof ApiError ? err.message : 'Could not parse that XML.');
    }
  };

  const createFormat = async (): Promise<void> => {
    if (!draft) return;
    setDraftBusy('create');
    try {
      const { warnings: _w, ...body } = draft;
      const created = await apiPost<{ format: { id: string } }>(
        '/api/v1/extractor/destination-formats',
        { ...body, status: 'active' },
      );
      await loadFormats(created.format.id);
      setDraft(null);
      setKbCode('');
      setPasteXml('');
      setAddOpen(false);
      setDraftBusy('idle');
    } catch (err) {
      setDraftBusy('idle');
      setDraftMsg(err instanceof ApiError ? err.message : 'Could not create the format.');
    }
  };

  // ---- Step 1: upload example + first AI proposal ---------------------------

  const analyze = async (): Promise<void> => {
    if (!file || !destination) return;
    setError(null);
    setActivated(false);
    try {
      if (!gcsPathRef.current) {
        setPhase('uploading');
        const mint = await apiPost<{ uploadUrl: string; gcsPath: string }>(
          '/api/v1/extractor/builder/upload-url',
          { sizeBytes: file.size },
        );
        await putToGcs(mint.uploadUrl, file, XLSX_MIME);
        gcsPathRef.current = mint.gcsPath;
      }
      setPhase('analyzing');
      const p = await apiPost<Proposal>('/api/v1/extractor/builder/analyze', {
        gcsPath: gcsPathRef.current,
        destinationFormatId: destination.id,
      });
      setProposal(p);
      setIteration(1);
      setRun(null);
      setPreviewRows([]);
      setPhase('idle');
    } catch (err) {
      fail(err, 'Analysis failed.');
    }
  };

  // ---- Step 2: preview through the real pipeline ----------------------------

  const ensureTemplate = async (recipe: Record<string, unknown>): Promise<string> => {
    const dn = displayName.trim() || `${vendor.trim() || 'Vendor'} — ${destination?.code}`;
    if (templateId) {
      await apiPatch(`/api/v1/extractor/templates/${templateId}`, { recipe });
      return templateId;
    }
    const rowsPrompt =
      `Recipe-driven template authored via the Template Builder for the ` +
      `${destination?.code} destination format. The declarative recipe on this ` +
      `template drives a deterministic transform — this prompt is descriptive only.`;
    // POST /templates returns the created template doc directly (not wrapped).
    const created = await apiPost<{ id: string }>('/api/v1/extractor/templates', {
      name: slugify(dn),
      displayName: dn,
      format: destination?.code,
      outputShape: destination?.code,
      vendor: vendor.trim() || 'unknown',
      accessProvider: vendor.trim() || 'unknown',
      rowsPrompt,
      extractionRules: `Authored via Template Builder from example "${file?.name ?? ''}".`,
      columns: destination?.inputColumns ?? [],
      recipe,
    });
    setTemplateId(created.id);
    return created.id;
  };

  const preview = async (recipeOverride?: Record<string, unknown>): Promise<void> => {
    const recipe = recipeOverride ?? proposal?.recipe;
    if (!file || !recipe) return;
    setError(null);
    try {
      setPhase('saving_template');
      const tid = await ensureTemplate(recipe);

      setPhase('previewing');
      const mint = await apiPost<{ runId: string; uploadUrl: string }>(
        '/api/v1/extractor/runs/upload-url',
        { sourceFileName: file.name, mimeType: XLSX_MIME, sizeBytes: file.size, templateId: tid },
      );
      await putToGcs(mint.uploadUrl, file, XLSX_MIME);
      await apiPost(`/api/v1/extractor/runs/${mint.runId}/start`, {});

      // Poll to terminal (the recipe path is deterministic — typically <60s).
      let doc: RunDoc | null = null;
      for (let i = 0; i < 60; i += 1) {
        await new Promise((r) => setTimeout(r, 4000));
        doc = await apiGet<RunDoc>(`/api/v1/extractor/runs/${mint.runId}`);
        if (['ready', 'partial', 'failed'].includes(doc.status)) break;
      }
      setRun(doc);
      if (doc && (doc.status === 'ready' || doc.status === 'partial')) {
        const pv = await apiGet<{ rows: PreviewRow[] }>(
          `/api/v1/extractor/runs/${mint.runId}/preview?limit=12`,
        );
        setPreviewRows(pv.rows ?? []);
      } else {
        setPreviewRows([]);
      }
      setPhase('idle');
    } catch (err) {
      fail(err, 'Preview run failed.');
    }
  };

  // ---- Step 3: refine with feedback ------------------------------------------

  const refine = async (): Promise<void> => {
    if (!proposal || !destination || !gcsPathRef.current || !feedback.trim()) return;
    setError(null);
    try {
      setPhase('refining');
      const p = await apiPost<Proposal>('/api/v1/extractor/builder/analyze', {
        gcsPath: gcsPathRef.current,
        destinationFormatId: destination.id,
        priorRecipe: proposal.recipe,
        feedback: feedback.trim(),
      });
      setProposal(p);
      setIteration((n) => n + 1);
      setFeedback('');
      // Auto-preview the refined recipe — the loop the page exists for.
      await preview(p.recipe);
    } catch (err) {
      fail(err, 'Refine failed.');
    }
  };

  // ---- Step 4: activate -------------------------------------------------------

  const activate = async (): Promise<void> => {
    if (!templateId) return;
    setError(null);
    try {
      await apiPost(`/api/v1/extractor/templates/${templateId}/activate`, { status: 'active' });
      setActivated(true);
    } catch (err) {
      fail(err, 'Activation failed.');
    }
  };

  const stats = run?.crptflStats;
  const previewHeaders = previewRows.length > 0 ? Object.keys(previewRows[0].cells) : [];

  // Journey position for the stepper: a step is done once its output exists.
  const stepsDone: number[] = [];
  if (file && destId) stepsDone.push(1);
  if (proposal) stepsDone.push(2);
  if (run && (run.status === 'ready' || run.status === 'partial')) stepsDone.push(3);
  if (activated) stepsDone.push(4);
  const currentStep = activated
    ? 4
    : run
      ? run.status === 'ready'
        ? 4
        : 3
      : proposal
        ? 3
        : file
          ? 2
          : 1;

  if (!isAdmin) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl p-6">
          <h1 className="font-display text-xl text-nebula-fg-1">Template Builder</h1>
          <p className="mt-2 text-sm text-nebula-fg-3">
            The Template Builder is available to knowledgebase admins only.
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mx-auto max-w-5xl p-6">
        <div className="flex items-baseline justify-between">
          <div>
            <h1 className="font-display text-xl text-nebula-fg-1">Template Builder</h1>
            <p className="mt-1 text-sm text-nebula-fg-3">
              Upload an example vendor file, pick the OneView destination format, and iterate with
              the AI until the output matches — then activate the template for everyone.
            </p>
          </div>
          <Link
            to="/admin/extractor-templates"
            className="shrink-0 whitespace-nowrap text-sm text-nebula-secondary hover:underline"
          >
            Template library →
          </Link>
        </div>

        <BuilderStepper current={currentStep} done={stepsDone} />

        {error && (
          <div className="mt-4 rounded border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* ---- Step 1: setup ---- */}
        <section className="mt-6 rounded-lg border border-nebula-border bg-white p-4">
          <h2 className="ov-meta-upper text-nebula-fg-3">1 · Example file & destination</h2>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-sm text-nebula-fg-2">Example workbook (.xlsx)</span>
              {/* Styled drop-zone instead of the native "Choose File" control —
                  matches the OneView surface language and confirms the chosen
                  file (name + size) instead of the browser's default text. */}
              <span
                className={
                  'flex cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-4 transition-colors ' +
                  (file
                    ? 'border-nebula-secondary bg-nebula-tint/40'
                    : 'border-nebula-border bg-nebula-surface hover:border-nebula-secondary')
                }
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  if (busy) return;
                  const dropped = e.dataTransfer.files?.[0];
                  if (dropped && dropped.name.toLowerCase().endsWith('.xlsx')) {
                    setFile(dropped);
                    gcsPathRef.current = null;
                    setProposal(null);
                    setRun(null);
                    setPreviewRows([]);
                    setTemplateId(null);
                    setIteration(0);
                  }
                }}
              >
                <UploadCloudIcon size={22} className="shrink-0 text-nebula-fg-4" />
                {file ? (
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-nebula-fg-1">
                      {file.name}
                    </span>
                    <span className="block text-xs text-nebula-fg-3">
                      {(file.size / 1024).toFixed(1)} KB · click or drop to replace
                    </span>
                  </span>
                ) : (
                  <span>
                    <span className="block text-sm text-nebula-fg-2">
                      Drop an .xlsx here, or click to browse
                    </span>
                    <span className="block text-xs text-nebula-fg-4">
                      Use a real, recent file from the vendor — the AI learns the layout from it
                    </span>
                  </span>
                )}
                <input
                  type="file"
                  accept=".xlsx"
                  disabled={busy}
                  onChange={(e) => {
                    setFile(e.target.files?.[0] ?? null);
                    gcsPathRef.current = null;
                    setProposal(null);
                    setRun(null);
                    setPreviewRows([]);
                    setTemplateId(null);
                    setIteration(0);
                  }}
                  className="sr-only"
                />
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-nebula-fg-2">Destination format</span>
              <select
                value={destId}
                disabled={busy}
                onChange={(e) => setDestId(e.target.value)}
                className="w-full rounded border border-nebula-border px-2 py-1.5 text-sm"
              >
                {destinations.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.displayName}
                  </option>
                ))}
              </select>
              {destination && (
                <span className="mt-1 block text-xs text-nebula-fg-3">
                  {destination.description}
                </span>
              )}
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-nebula-fg-2">Vendor</span>
              <input
                type="text"
                value={vendor}
                disabled={busy}
                onChange={(e) => setVendor(e.target.value)}
                placeholder="e.g. Vodacom"
                className="w-full rounded border border-nebula-border px-2 py-1.5 text-sm"
              />
              <span className="mt-1 block text-xs text-nebula-fg-4">
                Shown as the vendor chip on every run of this template.
              </span>
            </label>
            <label className="block">
              <span className="mb-1 block text-sm text-nebula-fg-2">Template name</span>
              <input
                type="text"
                value={displayName}
                disabled={busy}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder={`e.g. Vodacom — ${destination?.code ?? 'format'}`}
                className="w-full rounded border border-nebula-border px-2 py-1.5 text-sm"
              />
              <span className="mt-1 block text-xs text-nebula-fg-4">
                How the team finds it in the Data Extractor template picker.
              </span>
            </label>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setAddOpen((s) => !s)}
              className="text-xs text-nebula-secondary hover:underline"
            >
              {addOpen
                ? 'Hide format import'
                : 'Destination format not listed? Add it from OneView →'}
            </button>
          </div>

          {addOpen && (
            <div className="mt-3 rounded border border-nebula-border bg-nebula-tint/40 p-3">
              <p className="text-sm text-nebula-fg-2">
                Fetch the import definition from the knowledgebase by its OneView file reference, or
                paste the <code>&lt;Transformation&gt;</code> XML directly.
              </p>
              <div className="mt-2 flex flex-wrap items-end gap-2">
                <label className="block">
                  <span className="mb-1 block text-xs text-nebula-fg-3">
                    OneView file reference
                  </span>
                  <input
                    type="text"
                    value={kbCode}
                    onChange={(e) => setKbCode(e.target.value)}
                    placeholder="e.g. WanVPNBlFl"
                    className="w-56 rounded border border-nebula-border px-2 py-1.5 text-sm"
                  />
                </label>
                <button
                  type="button"
                  disabled={!kbCode.trim() || draftBusy !== 'idle'}
                  onClick={() => void fetchFromKb()}
                  className="rounded bg-nebula-secondary px-3 py-1.5 text-sm text-white disabled:opacity-40"
                >
                  {draftBusy === 'kb' ? 'Searching the KB… (~1 min)' : 'Fetch from KB'}
                </button>
              </div>
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-nebula-fg-3">
                  …or paste the Transformation XML
                </summary>
                <textarea
                  value={pasteXml}
                  onChange={(e) => setPasteXml(e.target.value)}
                  rows={5}
                  placeholder="<Transformation …>…</Transformation>"
                  className="mt-2 w-full rounded border border-nebula-border px-2 py-1.5 font-mono text-xs"
                />
                <button
                  type="button"
                  disabled={!pasteXml.trim() || draftBusy !== 'idle'}
                  onClick={() => void parsePastedXml()}
                  className="mt-2 rounded bg-nebula-secondary px-3 py-1.5 text-sm text-white disabled:opacity-40"
                >
                  {draftBusy === 'parse' ? 'Parsing…' : 'Parse XML'}
                </button>
              </details>

              {draftMsg && <p className="mt-2 text-sm text-red-700">{draftMsg}</p>}

              {draft && (
                <div className="mt-3 rounded border border-nebula-border bg-white p-3">
                  <div className="grid gap-3 md:grid-cols-3">
                    <label className="block">
                      <span className="mb-1 block text-xs text-nebula-fg-3">Code</span>
                      <input
                        type="text"
                        value={draft.code}
                        onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                        className="w-full rounded border border-nebula-border px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs text-nebula-fg-3">Display name</span>
                      <input
                        type="text"
                        value={draft.displayName}
                        onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                        className="w-full rounded border border-nebula-border px-2 py-1 text-sm"
                      />
                    </label>
                    <label className="block">
                      <span className="mb-1 block text-xs text-nebula-fg-3">
                        Source reshape (wide sheets → unpivot)
                      </span>
                      <select
                        value={draft.reshapeHint}
                        onChange={(e) => setDraft({ ...draft, reshapeHint: e.target.value })}
                        className="w-full rounded border border-nebula-border px-2 py-1 text-sm"
                      >
                        <option value="flat">flat — one row per source row</option>
                        <option value="unpivot">unpivot — charge columns → itemised rows</option>
                      </select>
                    </label>
                  </div>
                  <p className="mt-2 text-xs text-nebula-fg-3">
                    {draft.fileType} · header row above data row {draft.firstDataRow} ·{' '}
                    {draft.inputColumns.length} columns:{' '}
                    {draft.inputColumns.map((c) => c.name).join(', ')}
                  </p>
                  {draft.notes && (
                    <pre className="mt-2 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-nebula-tint p-2 text-xs">
                      {draft.notes}
                    </pre>
                  )}
                  {(draft.warnings ?? []).map((w, i) => (
                    <p key={i} className="mt-1 text-xs text-amber-700">
                      ⚠ {w}
                    </p>
                  ))}
                  <button
                    type="button"
                    disabled={draftBusy !== 'idle' || !draft.code.trim()}
                    onClick={() => void createFormat()}
                    className="mt-3 rounded bg-nebula-primary px-4 py-1.5 text-sm text-white disabled:opacity-40"
                  >
                    {draftBusy === 'create' ? 'Creating…' : 'Create destination format'}
                  </button>
                </div>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={!file || !destId || busy}
              onClick={() => void analyze()}
              className="rounded bg-nebula-primary px-4 py-2 text-sm text-white disabled:opacity-40"
            >
              {iteration > 0 ? 'Re-analyse from scratch' : 'Analyse with AI'}
            </button>
            {/* A silently disabled primary button reads as broken — always say
                what unlocks it, and set the time expectation once it's ready. */}
            <span className="text-xs text-nebula-fg-3">
              {!file
                ? 'Choose an example workbook to enable analysis.'
                : !destId
                  ? 'Pick a destination format to enable analysis.'
                  : busy
                    ? ''
                    : 'The AI reads the workbook layout and proposes the transform — takes about a minute.'}
            </span>
          </div>
        </section>

        {busy && (
          <div className="mt-4 flex items-center gap-2 text-sm text-nebula-fg-3">
            <Loader2Icon className="h-4 w-4 animate-spin" />
            {PHASE_LABEL[phase]}
          </div>
        )}

        {/* ---- Step 2: proposal ---- */}
        {proposal && (
          <section className="mt-6 rounded-lg border border-nebula-border bg-white p-4">
            <h2 className="ov-meta-upper text-nebula-fg-3">
              2 · AI proposal (iteration {iteration})
            </h2>
            <p className="mt-2 text-sm text-nebula-fg-2">{proposal.rationale}</p>
            {proposal.warnings.length > 0 && (
              <ul className="mt-2 list-disc pl-5 text-sm text-amber-700">
                {proposal.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
            <button
              type="button"
              onClick={() => setShowRecipe((s) => !s)}
              className="mt-2 text-xs text-nebula-secondary hover:underline"
            >
              {showRecipe ? 'Hide recipe JSON' : 'Show recipe JSON'}
            </button>
            {showRecipe && (
              <pre className="mt-2 max-h-80 overflow-auto rounded bg-nebula-tint p-3 text-xs">
                {JSON.stringify(proposal.recipe, null, 2)}
              </pre>
            )}
            <div className="mt-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => void preview()}
                className="rounded bg-nebula-secondary px-4 py-2 text-sm text-white disabled:opacity-40"
              >
                Run preview through the pipeline
              </button>
            </div>
          </section>
        )}

        {/* ---- Step 3: preview + refine ---- */}
        {run && (
          <section className="mt-6 rounded-lg border border-nebula-border bg-white p-4">
            <h2 className="ov-meta-upper text-nebula-fg-3">3 · Pipeline preview</h2>
            <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
              <span
                className={
                  run.status === 'ready'
                    ? 'rounded bg-green-100 px-2 py-0.5 text-green-800'
                    : 'rounded bg-red-100 px-2 py-0.5 text-red-800'
                }
              >
                {run.status}
              </span>
              {run.error && <span className="text-red-700">{run.error}</span>}
              {stats && (
                <span className="text-nebula-fg-3">
                  {String(stats.msisdn_count ?? stats.entity_count ?? '—')} entities ·{' '}
                  {String(stats.line_count ?? '—')} lines · grand total{' '}
                  {typeof stats.grand_total_sum === 'number'
                    ? stats.grand_total_sum.toLocaleString('en-ZA', {
                        minimumFractionDigits: 2,
                      })
                    : '—'}
                  {typeof stats.charge_line_sum === 'number' &&
                    typeof stats.subtotal_excl_vat_sum === 'number' && (
                      <>
                        {' · '}
                        {Math.abs(stats.charge_line_sum - stats.subtotal_excl_vat_sum) < 0.01 ? (
                          <span className="text-green-700">charges reconcile ✓</span>
                        ) : (
                          <span className="text-red-700">
                            charge sum {stats.charge_line_sum.toFixed(2)} ≠ stated totals{' '}
                            {stats.subtotal_excl_vat_sum.toFixed(2)}
                          </span>
                        )}
                      </>
                    )}
                </span>
              )}
            </div>

            {previewRows.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-nebula-border text-left text-nebula-fg-3">
                      {previewHeaders.map((h) => (
                        <th key={h} className="px-2 py-1 font-medium">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-b border-nebula-border-soft">
                        {previewHeaders.map((h) => (
                          <td key={h} className="px-2 py-1">
                            {r.cells[h]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="mt-1 text-xs text-nebula-fg-3">
                  First {previewRows.length} line rows. Subtotal / preamble / EOF rows are in the
                  downloadable file but not shown here.
                </p>
              </div>
            )}

            <div className="mt-4">
              <label className="block">
                <span className="mb-1 block text-sm text-nebula-fg-2">
                  Not right yet? Describe what to change — the AI updates the recipe and re-runs the
                  preview.
                </span>
                <textarea
                  value={feedback}
                  disabled={busy}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows={3}
                  placeholder='e.g. "Inv No must repeat on every row" or "the header must land on row 7"'
                  className="w-full rounded border border-nebula-border px-2 py-1.5 text-sm"
                />
              </label>
              <div className="mt-2 flex items-center gap-3">
                <button
                  type="button"
                  disabled={busy || !feedback.trim()}
                  onClick={() => void refine()}
                  className="rounded bg-nebula-secondary px-4 py-2 text-sm text-white disabled:opacity-40"
                >
                  Refine & re-preview
                </button>
                <button
                  type="button"
                  disabled={busy || run.status !== 'ready' || activated}
                  onClick={() => void activate()}
                  className="rounded bg-nebula-primary px-4 py-2 text-sm text-white disabled:opacity-40"
                >
                  {activated ? 'Template activated ✓' : 'Looks right — activate template'}
                </button>
              </div>
              {activated && (
                <div className="mt-3 rounded-md border border-nebula-success/30 bg-nebula-success-bg px-3 py-2 text-sm text-nebula-success">
                  Template activated. The team can use it right now: Data Extractor → Upload → pick{' '}
                  <span className="font-medium">{displayName.trim() || 'this template'}</span> in
                  the template picker.{' '}
                  <Link to="/workspace/extractor" className="underline">
                    Open Data Extractor
                  </Link>{' '}
                  ·{' '}
                  <Link to="/admin/extractor-templates" className="underline">
                    Template library
                  </Link>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
